import { useQuery } from '@tanstack/react-query';
import { usePublicCountry } from '@/contexts/PublicCountryContext';
import { setPublicCountryIdForApi } from '@/utils/publicCountryStore';
import { getLiberiaActiveAnnouncement, isLiberiaApiEnabled } from '@/utils/liberiaApi';

interface PublicAnnouncement {
  id: string;
  subject: string;
  message: string;
  status: string;
  priority: string;
  country_id?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * One featured announcement for the visitor: their country plus All.
 * Waits until the site has finished resolving that country so an early
 * All-only response is not cached for five minutes.
 */
export const useActiveAnnouncement = () => {
  const { countryId, resolving } = usePublicCountry();
  const query = useQuery({
    queryKey: ['active-announcement', countryId ?? 'all'],
    queryFn: async () => {
      if (!isLiberiaApiEnabled()) {
        return null;
      }

      // Header is checked before the query. Keep them the same for this request.
      setPublicCountryIdForApi(countryId ?? null);
      return getLiberiaActiveAnnouncement(countryId) as Promise<PublicAnnouncement | null>;
    },
    enabled: isLiberiaApiEnabled() && !resolving,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  return {
    ...query,
    isLoading: resolving || query.isLoading,
    isPending: resolving || query.isPending,
  };
};
